import axios from 'axios';
import { useAuthStore } from '@/stores';

// Get API Base URL (same logic as apiClient)
const getApiBaseUrl = (): string => {
  const runtimeConfig = window._env_?.API_BASE_URL;
  const buildTimeConfig = import.meta.env.VITE_API_BASE_URL;
  const defaultUrl = 'http://localhost:5051';
  return runtimeConfig || buildTimeConfig || defaultUrl;
};

// Token refresh timeout (10 seconds)
const REFRESH_TIMEOUT = 10000;

/**
 * Token Refresh Service
 *
 * Centralized token refresh logic with race condition protection.
 * Used by both Axios interceptor and authFetch wrapper.
 *
 * Key features:
 * - Singleton refresh lock prevents concurrent refresh calls
 * - Request queue allows multiple 401s to share single refresh
 * - 10-second timeout prevents deadlock
 * - No exponential backoff - fail immediately on error
 */
class TokenRefreshService {
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];
  private refreshRejecters: ((error: Error) => void)[] = [];

  /**
   * Notify all waiting subscribers when refresh completes
   */
  private onRefreshed(token: string): void {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
    this.refreshRejecters = [];
  }

  /**
   * Notify all waiting subscribers when refresh fails
   */
  private onRefreshFailed(error: Error): void {
    this.refreshRejecters.forEach((callback) => callback(error));
    this.refreshSubscribers = [];
    this.refreshRejecters = [];
  }

  /**
   * Subscribe to ongoing refresh - returns promise that resolves with new token
   */
  subscribeToRefresh(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.refreshSubscribers.push(resolve);
      this.refreshRejecters.push(reject);
    });
  }

  /**
   * Check if a refresh is currently in progress
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }

  /**
   * Attempt to refresh the access token
   *
   * If already refreshing, waits for ongoing refresh.
   * If not, initiates new refresh.
   *
   * @returns New access token on success
   * @throws Error on failure (token invalid, timeout, network error)
   */
  async refreshToken(): Promise<string> {
    // If already refreshing, subscribe and wait
    if (this.isRefreshing) {
      console.log('[TokenRefresh] Already refreshing, subscribing to queue...');
      return this.subscribeToRefresh();
    }

    const { refreshToken, setTokenAuth, clearAuth } = useAuthStore.getState();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.isRefreshing = true;
    console.log('[TokenRefresh] Starting token refresh...');

    try {
      // Use raw axios to avoid apiClient interceptor loop
      const response = await axios.post(
        `${getApiBaseUrl()}/api/v1/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
          timeout: REFRESH_TIMEOUT,
        }
      );

      const { access_token, refresh_token } = response.data;

      // Update store with new tokens
      setTokenAuth({
        access_token,
        refresh_token,
      });

      console.log('[TokenRefresh] Token refreshed successfully');

      // Notify all waiting subscribers
      this.onRefreshed(access_token);
      this.isRefreshing = false;

      return access_token;
    } catch (error) {
      console.error('[TokenRefresh] Token refresh failed:', error);

      // Reset state
      this.isRefreshing = false;

      // Notify all waiting subscribers of failure
      const refreshError = new Error('Token refresh failed');
      this.onRefreshFailed(refreshError);

      // Clear auth state - session is invalid
      clearAuth();

      throw refreshError;
    }
  }

  /**
   * Force reset the refresh state (for testing or recovery)
   */
  reset(): void {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.refreshRejecters = [];
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
